/**
 * CR-8227: Platform Config's organisation setup — the draft a Helix user fills
 * in for a new customer instance, its checks, and the run that creates it.
 *
 * Creation goes through the same endpoints and payloads as the Organisation
 * tree's Add dialog (see `orgchart/nodePayloads`), never a second way to
 * create companies. The run is resumable: each created record keeps its id on
 * the draft, so after a failure a second run carries on where the first one
 * stopped instead of creating anything twice.
 */
import { coreAPI, platformSettingsAPI } from '../../../lib/api';
import type { OrganisationStructure } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import {
  DEFAULT_BASE_CURRENCY,
  EMPTY_FORM,
  type NodeConfigForm,
} from '../../orgchart/useNodeConfigForm';
import { buildCompanyCreatePayload, buildGroupCreatePayload } from '../../orgchart/nodePayloads';
import { pluralize } from '../../orgchart/types';

/** A recorded structure (the setting without its "not recorded" value). */
export type OrganisationShape = Exclude<OrganisationStructure, ''>;

/** The names the customer sees for each level. */
export interface LevelNames {
  group: string;
  division: string;
  site: string;
}

/** Highest length the PlatformSettings label columns accept. */
export const LEVEL_NAME_MAX = 50;

/**
 * Level colours as the approved mockup shows them: badges and dots, and the
 * dashed Add buttons. (Aligning the Organisation tree's own colours is CR-8230.)
 */
export const LEVEL_COLORS = {
  group: { fill: 'bg-violet-600' },
  division: { fill: 'bg-blue-600', add: 'border-blue-600 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300' },
  site: { fill: 'bg-green-700', add: 'border-green-700 bg-green-50 text-green-800 hover:bg-green-100 dark:bg-green-950/40 dark:text-green-300' },
} as const;

export const SHAPES: Array<{ id: OrganisationShape; title: string; description: string }> = [
  { id: 'single_site', title: 'Single site', description: 'One site, no group above it.' },
  { id: 'group_sites', title: 'Group with sites', description: 'A group holding its sites directly.' },
  {
    id: 'group_divisions_sites',
    title: 'Group with divisions and sites',
    description: 'A group, its divisions, and the sites in each.',
  },
];

/**
 * @param structure - a recorded structure, or '' when none is recorded
 * @returns its title, or "Not recorded"
 */
export function structureTitle(structure: OrganisationStructure): string {
  return SHAPES.find((shape) => shape.id === structure)?.title ?? 'Not recorded';
}

/**
 * The levels a structure uses. With nothing recorded every level is shown,
 * as the old Hierarchy Labels card did.
 *
 * @param structure - the recorded or chosen structure
 * @returns which of the group and division levels apply (the site level always does)
 */
export function levelsFor(structure: OrganisationStructure): { group: boolean; division: boolean } {
  return {
    group: structure !== 'single_site',
    division: structure === 'group_divisions_sites' || structure === '',
  };
}

/** One division or site row of the setup table. */
export interface SetupRow {
  key: string;
  kind: 'division' | 'site';
  /** A site's division (the division row's key); null for a site under the group or alone. */
  parentKey: string | null;
  form: NodeConfigForm;
  /** Set once this row has been created, so a re-run skips it. */
  createdId?: number;
}

export interface SetupDraft {
  shape: OrganisationShape;
  labels: LevelNames;
  group: NodeConfigForm;
  /** Set once the group has been created. */
  groupId?: number;
  rows: SetupRow[];
  /** The structure and level names are saved first; set once they are. */
  settingsSaved: boolean;
}

let rowCounter = 0;
const nextKey = (kind: SetupRow['kind']): string => `${kind}-${++rowCounter}`;

/**
 * @param parentKey - the division the site belongs to, if any
 * @returns a blank site row, on the platform's default currency
 */
export function newSiteRow(parentKey: string | null = null): SetupRow {
  return {
    key: nextKey('site'),
    kind: 'site',
    parentKey,
    form: { ...EMPTY_FORM, currency: DEFAULT_BASE_CURRENCY },
  };
}

/** @returns a blank division row */
export function newDivisionRow(): SetupRow {
  return { key: nextKey('division'), kind: 'division', parentKey: null, form: { ...EMPTY_FORM } };
}

/**
 * @param shape - the structure to start from
 * @param labels - the level names in force today
 * @returns a draft with the minimum rows the shape needs
 */
export function initialDraft(shape: OrganisationShape, labels: LevelNames): SetupDraft {
  const rows: SetupRow[] = [];
  if (shape === 'group_divisions_sites') {
    const division = newDivisionRow();
    rows.push(division, newSiteRow(division.key));
  } else {
    rows.push(newSiteRow());
  }
  return { shape, labels, group: { ...EMPTY_FORM }, rows, settingsSaved: false };
}

/** True once any record of the draft has been created. */
export function hasCreatedAnything(draft: SetupDraft): boolean {
  return Boolean(draft.groupId) || draft.rows.some((row) => row.createdId);
}

/**
 * Switch the draft to another structure, keeping what still fits.
 *
 * Single site keeps the first site only; group with sites drops the divisions
 * and puts every site under the group; group with divisions and sites puts
 * every site under the first division (adding one if there is none).
 *
 * @param draft - the current draft (nothing created yet)
 * @param shape - the structure to switch to
 * @returns the new draft and how many filled-in rows it drops
 */
export function reshape(draft: SetupDraft, shape: OrganisationShape): { draft: SetupDraft; dropped: number } {
  const filled = (row: SetupRow) => Boolean(row.form.name.trim() || row.form.primaryEmail.trim());
  const sites = draft.rows.filter((row) => row.kind === 'site');
  const divisions = draft.rows.filter((row) => row.kind === 'division');
  let rows: SetupRow[];
  let droppedRows: SetupRow[] = [];
  if (shape === 'single_site') {
    const [first, ...rest] = sites;
    rows = [{ ...(first ?? newSiteRow()), parentKey: null }];
    droppedRows = [...rest, ...divisions];
  } else if (shape === 'group_sites') {
    rows = (sites.length ? sites : [newSiteRow()]).map((row) => ({ ...row, parentKey: null }));
    droppedRows = divisions;
  } else {
    const division = divisions[0] ?? newDivisionRow();
    const kept = divisions.length ? divisions : [division];
    rows = [
      ...kept,
      ...(sites.length ? sites : [newSiteRow()]).map((row) => ({
        ...row,
        parentKey: row.parentKey && kept.some((d) => d.key === row.parentKey) ? row.parentKey : division.key,
      })),
    ];
  }
  return { draft: { ...draft, shape, rows, settingsSaved: false }, dropped: droppedRows.filter(filled).length };
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Everything that would stop the setup, in the order the form shows it.
 *
 * @param draft - the draft to check
 * @returns one message per problem; empty when the draft can be created
 */
export function validateDraft(draft: SetupDraft): string[] {
  const problems: string[] = [];
  const levels = levelsFor(draft.shape);
  const { labels } = draft;
  const levelEntries: Array<[boolean, string, string]> = [
    [levels.group, 'top level', labels.group],
    [levels.division, 'middle level', labels.division],
    [true, 'site level', labels.site],
  ];
  levelEntries.forEach(([used, which, value]) => {
    if (used && !value.trim()) problems.push(`Name the ${which}.`);
  });

  if (levels.group && !draft.group.name.trim()) problems.push(`Enter the ${labels.group || 'group'} name.`);

  const divisions = draft.rows.filter((row) => row.kind === 'division');
  const sites = draft.rows.filter((row) => row.kind === 'site');
  divisions.forEach((row, index) => {
    if (!row.form.name.trim()) problems.push(`Enter a name for ${labels.division || 'division'} ${index + 1}.`);
  });
  if (!sites.length) problems.push(`Add at least one ${labels.site || 'site'}.`);
  sites.forEach((row, index) => {
    const which = row.form.name.trim() || `${labels.site || 'site'} ${index + 1}`;
    if (!row.form.name.trim()) problems.push(`Enter a name for ${labels.site || 'site'} ${index + 1}.`);
    if (!row.form.primaryEmail.trim()) problems.push(`Enter the primary email for ${which}.`);
    else if (!EMAIL_PATTERN.test(row.form.primaryEmail.trim())) problems.push(`The primary email for ${which} is not a valid email address.`);
    if (draft.shape === 'group_divisions_sites' && !divisions.some((d) => d.key === row.parentKey)) {
      problems.push(`Choose the ${labels.division || 'division'} ${which} belongs to.`);
    }
  });

  // Group and division names are unique across every group on the instance;
  // site names are how imports find a site, so two the same would be ambiguous.
  const duplicates = (names: string[]) => names
    .map((name) => name.trim().toLowerCase())
    .filter((name, index, all) => name && all.indexOf(name) !== index);
  const groupNames = [...(levels.group ? [draft.group.name] : []), ...divisions.map((row) => row.form.name)];
  new Set(duplicates(groupNames)).forEach((name) => problems.push(`"${name}" is used twice; each ${labels.group || 'group'} and ${labels.division || 'division'} needs its own name.`));
  new Set(duplicates(sites.map((row) => row.form.name))).forEach((name) => problems.push(`"${name}" is used for two ${pluralize(labels.site || 'site')}; each needs its own name.`));
  return problems;
}

interface GroupMatch {
  name: string;
  company_type?: 'customer' | 'supplier' | null;
}

/**
 * Group and division names already taken by any group on the instance —
 * customer or supplier, since group names are unique across both.
 *
 * @param draft - the draft whose not-yet-created group names to check
 * @returns one message per name already in use
 */
export async function findGroupNameClashes(draft: SetupDraft): Promise<string[]> {
  const names = [
    ...(levelsFor(draft.shape).group && !draft.groupId ? [draft.group.name.trim()] : []),
    ...draft.rows.filter((row) => row.kind === 'division' && !row.createdId).map((row) => row.form.name.trim()),
  ].filter(Boolean);
  const clashes: string[] = [];
  await Promise.all(names.map(async (name) => {
    const resp = await coreAPI.getCompanyGroups({ search: name, page_size: 100 });
    const raw = resp.data as unknown;
    const results = Array.isArray(raw) ? (raw as GroupMatch[]) : ((raw as { results?: GroupMatch[] }).results ?? []);
    const match = results.find((group) => group.name.trim().toLowerCase() === name.toLowerCase());
    if (match) {
      const side = match.company_type === 'supplier' ? 'A supplier group' : match.company_type === 'customer' ? 'A customer group' : 'A group';
      clashes.push(`${side} already uses the name "${match.name}". Choose a different name.`);
    }
  }));
  return clashes;
}

/** A failed run: the message, plus the draft with every record created so far. */
export class SetupRunError extends Error {
  draft: SetupDraft;

  constructor(message: string, draft: SetupDraft) {
    super(message);
    this.draft = draft;
  }
}

/**
 * Save the structure and level names, then create the group, its divisions and
 * the sites, in that order. Anything already created (an id on the draft) is
 * skipped, so calling this again after a failure finishes the job.
 *
 * @param input - a draft that passed {@link validateDraft}
 * @returns the draft with every id filled in
 * @throws SetupRunError naming the step that failed, carrying the progress so far
 */
export async function runSetup(input: SetupDraft): Promise<SetupDraft> {
  const draft: SetupDraft = { ...input, rows: input.rows.map((row) => ({ ...row })) };
  const step = async <T>(what: string, call: () => Promise<T>): Promise<T> => {
    try {
      return await call();
    } catch (err: unknown) {
      throw new SetupRunError(`${what} failed: ${getApiErrorMessage(err, 'please try again.')}`, draft);
    }
  };

  if (!draft.settingsSaved) {
    await step('Saving the structure and level names', () => platformSettingsAPI.patchSettings({
      organisation_structure: draft.shape,
      hierarchy_level_1_label: draft.labels.group.trim(),
      hierarchy_level_2_label: draft.labels.division.trim(),
      hierarchy_level_3_label: draft.labels.site.trim(),
    }));
    draft.settingsSaved = true;
  }

  if (levelsFor(draft.shape).group && !draft.groupId) {
    const resp = await step(`Creating ${draft.group.name.trim()}`, () => coreAPI.createCompanyGroup(
      buildGroupCreatePayload({ ...draft.group, name: draft.group.name.trim() }, { companyType: 'customer' }),
    ));
    draft.groupId = resp.data.id;
  }

  for (const row of draft.rows.filter((r) => r.kind === 'division' && !r.createdId)) {
    const resp = await step(`Creating ${row.form.name.trim()}`, () => coreAPI.createCompanyGroup(
      buildGroupCreatePayload({ ...row.form, name: row.form.name.trim() }, { parentGroup: draft.groupId, companyType: 'customer' }),
    ));
    row.createdId = resp.data.id;
  }

  for (const row of draft.rows.filter((r) => r.kind === 'site' && !r.createdId)) {
    const division = draft.rows.find((r) => r.key === row.parentKey);
    const resp = await step(`Creating ${row.form.name.trim()}`, () => coreAPI.createCompany(
      buildCompanyCreatePayload(
        { ...row.form, name: row.form.name.trim(), primaryEmail: row.form.primaryEmail.trim() },
        {
          companyType: 'customer',
          group: division?.createdId ?? draft.groupId,
          baseCurrency: row.form.currency || DEFAULT_BASE_CURRENCY,
        },
      ),
    ));
    row.createdId = resp.data.id;
  }
  return draft;
}
